import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Image from '../../../Image';
import PreviewImage from '../../../PreviewImage';
import previewImageStyles from '../../../PreviewImage/PreviewImage.module.sass';
import styles from './HolidayBuilder.module.scss';
import overlay from './assets/graduate-black-magnet-overlay.png';
import fridge from './assets/graduate-magnet-fridge.jpg';
import classYearFor from './classYear';
import { CustomerContext } from '../../../../contexts/CustomerContext';

// THE GRADUATE MAGNET: the customer's pose and chosen background, seen through
// the window of an opaque black overlay that carries a script "graduate", with
// "CLASS OF YYYY" and the student's name set beneath the window in Century
// Gothic -- the type the lab prints it in.
//
// Drawn ONCE, here, for both surfaces that show it: the gifts page (`Item`,
// shelf tile + product screen) and the cart (`DisplayableSheet`). Each of those
// has a one-line `case 'gradmagnet'` that hands over the mockup, the pose and
// the background and nothing else, so the two cannot drift the way the Desk
// Plaque did when each component positioned the pose itself.
//
// WHAT THE PREVIEW IS MADE OF, bottom to top:
//   1. the fridge door (bundled; see below)
//   2. two magnets, each a 4:5 box positioned over the fridge
//   3. inside each: the pose composited over the chosen background through
//      `PreviewImage` -- the ONE compositor every pose on this page goes
//      through, so a family's chosen backdrop shows here as it does everywhere
//      else -- clipped to the overlay's window;
//   4. the overlay PNG, transparent only in the window, above the pose;
//   5. the two text lines, above everything.
//
// BOTH IMAGES ARE BUNDLED, NOT UPLOADED. The overlay's window geometry is what
// `.gradmagnetWindow` measures against (758x948 at 221,276 on 1200x1500), and
// the magnets' positions were measured off THIS fridge, so art and numbers
// change together or not at all. It also means the product draws the moment an
// admin creates the add-on -- there is no "Product photo" placeholder waiting
// on an upload, and no wrong-sized mockup to slide the magnets off the door.
// `addon.imageUrl` is therefore ignored here (the variant is listed in Item's
// SELF_DRAWING_VARIANTS), the same arrangement as the cursive announcement.
//
// The text is HTML, not canvas: it scales with the magnet through container
// query units (the magnet box is an inline-size container), so the same
// percentages hold on a 150px shelf tile and a 500px product screen.
//
// ⚠️ A PLACEHOLDER NEVER REACHES THE SCREEN. No class year -> no "CLASS OF"
// line; no name -> no name line. `classYearFor` owns the first decision.
export const GRAD_MAGNET_CLASS_LINE = 'Class of';

function MagnetFace({
  pose, backgroundUrl, name, classYear, className,
}) {
  return (
    <div className={className} data-testid="grad-magnet">
      <div className={styles.gradmagnetWindow}>
        <PreviewImage
          isPreviewHidden={false}
          foregroundUrl={pose?.watermarkUrl || pose?.imageUrl}
          backgroundUrl={backgroundUrl}
        />
      </div>
      <Image src={overlay} alt="" classes={styles.gradmagnetOverlay} useDefaultClass={false} />
      {classYear && (
        <div className={styles.gradmagnetYear}>{`${GRAD_MAGNET_CLASS_LINE} ${classYear}`}</div>
      )}
      {name && <div className={styles.gradmagnetName}>{name}</div>}
    </div>
  );
}

MagnetFace.propTypes = {
  pose: PropTypes.shape({ watermarkUrl: PropTypes.string, imageUrl: PropTypes.string }),
  backgroundUrl: PropTypes.string,
  name: PropTypes.string,
  classYear: PropTypes.number,
  className: PropTypes.string,
};

MagnetFace.defaultProps = {
  pose: null, backgroundUrl: undefined, name: '', classYear: null, className: '',
};

// The student's name as the magnet prints it: "JESSICA RAMIREZ", from the
// signed-in student. Blank when either half is unknown rather than a lone
// first name -- half a name on a keepsake reads as a misprint.
export function magnetNameFor(student) {
  const first = String(student?.firstName || '').trim();
  const last = String(student?.lastName || '').trim();
  if (!first || !last) return '';

  return `${first} ${last}`.toUpperCase();
}

// `responsive` is the cart's and the edit builder's size cap: every mockup
// DisplayableSheet draws carries PreviewImage's `responsive` class (max-height
// 40vh), which is what keeps a product preview the same size from one add-on
// to the next on those screens. The gifts page sizes its tiles by the slot
// instead, so Item leaves it off. Without it the fridge filled the edit screen
// edge to edge (reported 2026-10-20).
export default function GradMagnet({
  pose, backgroundUrl, poses, responsive,
}) {
  const { customer } = useContext(CustomerContext) || {};
  const student = customer?.currentStudent;
  const name = magnetNameFor(student);
  const classYear = classYearFor({ student, poses: poses?.length ? poses : [pose] });

  const face = {
    pose, backgroundUrl, name, classYear,
  };

  return (
    <div className={styles.itemImgWrapper}>
      <Image
        src={fridge}
        alt=""
        classes={cn(
          styles.packageImage,
          responsive && previewImageStyles.img,
          responsive && previewImageStyles.responsive,
        )}
      />
      <MagnetFace {...face} className={styles.gradmagnet} />
      <MagnetFace {...face} className={styles.gradmagnetTwo} />
    </div>
  );
}

GradMagnet.propTypes = {
  pose: PropTypes.shape({
    watermarkUrl: PropTypes.string, imageUrl: PropTypes.string, folder: PropTypes.string,
  }),
  backgroundUrl: PropTypes.string,
  // Every pose the page knows, for the class-year fallback (the pose on the
  // magnet may be a studio sitting with no senior folder of its own).
  poses: PropTypes.arrayOf(PropTypes.shape({ folder: PropTypes.string })),
  responsive: PropTypes.bool,
};

GradMagnet.defaultProps = {
  pose: null, backgroundUrl: undefined, poses: [], responsive: false,
};
